import type { PersistedEnvelope } from "../types";

/** In-memory retention of envelopes newer than the running app's `currentVersion`. */
const futurePayloads = new Map<string, PersistedEnvelope<unknown>>();

/**
 * Retains a future-version envelope until the user explicitly mutates the slice.
 *
 * @param sliceKey Slice storage key.
 * @param envelope Raw envelope that could not be fully adopted by the current code version.
 */
export const retainFuturePayload = ( sliceKey: string, envelope: PersistedEnvelope<unknown> ): void => {
    futurePayloads.set( sliceKey, envelope );
};

/**
 * Returns whether a future payload is retained for a slice.
 *
 * @param sliceKey Slice storage key.
 * @returns `true` when a retained future payload exists.
 */
export const hasRetainedFuturePayload = ( sliceKey: string ): boolean => {
    return futurePayloads.has( sliceKey );
};

/**
 * Returns the retained future payload for a slice, if any.
 *
 * @param sliceKey Slice storage key.
 * @returns Retained envelope or `undefined`.
 */
export const getRetainedFuturePayload = ( sliceKey: string ): PersistedEnvelope<unknown> | undefined => {
    return futurePayloads.get( sliceKey );
};

/**
 * Clears a retained future payload after explicit user mutation.
 *
 * @param sliceKey Slice storage key.
 */
export const clearRetainedFuturePayload = ( sliceKey: string ): void => {
    futurePayloads.delete( sliceKey );
};

/**
 * Clears all retained payloads.
 *
 * @remarks Intended for unit tests only.
 */
export const clearAllRetainedFuturePayloads = (): void => {
    futurePayloads.clear();
};
